import useGetMyAccountPipelines from '@/api/queries/me/useGetMyAccountPipelines';
import useGetMyAccountTeams from '@/api/queries/me/useGetMyAccountTeams';
import { useGetMyAccountUsersExcludedDeactivated } from '@/api/queries/me/useGetMyAccountUsersExcludedDeactivated';
import {
  AnchorButton,
  ButtonRadioGroup,
  Combobox,
  SelectOption,
  SelectSizeVariant,
  Text,
} from '@payaca/ambrosia';
import ErrorBoundary from '@payaca/components/errorBoundary/ErrorBoundary';

import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { DispatchPermissions } from '@payaca/permissions/dispatch/dispatch.permissions';
import { PipelinesPermissions } from '@payaca/permissions/pipelines/pipelines.permissions';
import { ScheduledEventsPermissions } from '@payaca/permissions/scheduledEvents/scheduled-events.permissions';
import { FC, useContext, useMemo } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';

import { usePermissions } from '@/hooks/usePermissions';
import AccountUserAvatarSelector, {
  UNASSIGNED_NUMERIC_SENTINEL,
  UNASSIGNED_USER_ID_SENTINEL,
} from '../accountUserAvatarSelector/AccountUserAvatarSelector';
import AddressLookupControl from '../addressLookupControl/AddressLookupControl';
import {
  NO_PIPELINE,
  pruneUserIdsToTeamMembers,
  SCHEDULE_PIPELINE_TYPES,
  ScheduleContext,
} from '../contextProviders/ScheduleContextProvider';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import ScheduleFilters from '../scheduleFilters/ScheduleFilters';

const PipelineOptionLabel: FC<{
  option: SelectOption<string, { colour?: string }>;
}> = ({ option }) => (
  <span className="flex items-center gap-2">
    <span
      className="size-3 shrink-0 rounded-full"
      style={{ backgroundColor: option.metadata?.colour }}
    />
    <Text as="span">{option.label}</Text>
  </span>
);

const ScheduleDispatchFilters: FC = () => {
  const { url } = useRouteMatch();
  const requestData = useContext(ScheduleContext).requestData;
  const displayType = useContext(ScheduleContext).displayType;
  const { selectedTeamIds } = useContext(ScheduleContext).calendarViewData;
  const isPipelineFilterEnabled =
    useContext(ScheduleContext).isPipelineFilterEnabled;
  const hideCreateEventButton =
    useContext(ScheduleContext).hideCreateEventButton;

  const onUpdate = useContext(ScheduleContext).onUpdate;

  const { userHasRequiredPermission } = usePermissions();

  const { data: accountUsers = [], isLoading: isLoadingAccountUsers } =
    useGetMyAccountUsersExcludedDeactivated();
  const { data: accountTeams = [] } = useGetMyAccountTeams({
    enabled: userHasRequiredPermission([AccountsPermissions.GET_TEAMS]),
  });
  const { data: accountPipelines = [] } = useGetMyAccountPipelines({
    types: SCHEDULE_PIPELINE_TYPES,
    enabled: isPipelineFilterEnabled,
  });

  const selectedPublicIds = useMemo(
    () =>
      (requestData.assignedUserIds ?? []).map((numericId) => {
        if (numericId === UNASSIGNED_NUMERIC_SENTINEL) {
          return UNASSIGNED_USER_ID_SENTINEL;
        }
        return (
          accountUsers.find((u) => u.id === String(numericId))?.publicId ?? ''
        );
      }),
    [requestData.assignedUserIds, accountUsers]
  );

  const handleSelectionChange = (nextPublicIds: string[]) => {
    if (isLoadingAccountUsers) return;
    const nextNumericIds = nextPublicIds
      .map<number | null>((publicId) => {
        if (publicId === UNASSIGNED_USER_ID_SENTINEL) {
          return UNASSIGNED_NUMERIC_SENTINEL;
        }
        const matchedId = accountUsers.find((u) => u.publicId === publicId)?.id;
        return matchedId != null ? Number(matchedId) : null;
      })
      .filter((id): id is number => id !== null);
    onUpdate({ selectedUserIds: nextNumericIds });
  };

  // Public ids of the selected teams' members. Scopes the avatar picker so only
  // those users are shown. `null` (no team selected) shows everyone. Currently
  // selected users are always kept visible so they stay deselectable, even if a
  // persisted selection falls outside the team filter.
  const teamMemberPublicIds = useMemo(() => {
    if (!selectedTeamIds.length) return null;
    const memberIds = accountUsers
      .filter((user) =>
        user.teams.items.some((team) => selectedTeamIds.includes(team.id))
      )
      .map((user) => user.publicId);
    const selectedRealIds = selectedPublicIds.filter(
      (id) => Boolean(id) && id !== UNASSIGNED_USER_ID_SENTINEL
    );
    return [...new Set([...memberIds, ...selectedRealIds])];
  }, [selectedTeamIds, accountUsers, selectedPublicIds]);

  // Selecting team(s) scopes the schedule to those teams (members + unassigned),
  // without pre-selecting anyone. It only prunes an existing avatar selection so
  // no one outside the team stays selected once their avatar is hidden.
  const handleTeamSelectionChange = (nextTeamIds: string[]) => {
    if (isLoadingAccountUsers) return;
    if (!nextTeamIds.length) {
      onUpdate({ selectedTeamIds: [] });
      return;
    }
    const prunedUserIds = pruneUserIdsToTeamMembers({
      userIds: requestData.assignedUserIds ?? [],
      teamIds: nextTeamIds,
      accountUsers,
    });
    onUpdate({ selectedTeamIds: nextTeamIds, selectedUserIds: prunedUserIds });
  };

  return (
    <div>
      <PermissionGuard
        renderIfHasPermissions={[
          ScheduledEventsPermissions.GET_MY_DEAL_EVENTS,
          ScheduledEventsPermissions.GET_EVENTS,
          DispatchPermissions.GET_EVENTS,
          DispatchPermissions.PREVIEW_DISPATCH,
          ScheduledEventsPermissions.ADD_EVENT,
          ScheduledEventsPermissions.ADD_MY_EVENT,
        ]}
      >
        <div className="mb-4 flex flex-wrap items-start gap-8">
          <div className="mr-auto flex flex-wrap items-center gap-4">
            {isPipelineFilterEnabled && (
              <PermissionGuard
                renderIfHasPermissions={[
                  PipelinesPermissions.GET_PIPELINES_FOR_ACCOUNT,
                ]}
              >
                <div className="w-40">
                  <Combobox
                    multiple={true}
                    sizeVariant={SelectSizeVariant.SM}
                    placeholder="All pipelines"
                    value={requestData.projectPipelineIds ?? []}
                    onChange={(selectedPipelineIds) =>
                      onUpdate({ selectedPipelineIds })
                    }
                    options={[
                      { label: 'No pipeline', value: NO_PIPELINE },
                      ...accountPipelines.map((pipeline) => ({
                        label: pipeline.title,
                        value: pipeline.publicId,
                        metadata: { colour: pipeline.colour },
                      })),
                    ]}
                    CustomOption={PipelineOptionLabel}
                    CustomSelected={({ selectedOptions }) => {
                      if (selectedOptions.length === 0) return null;
                      if (selectedOptions.length === 1) {
                        return (
                          <PipelineOptionLabel option={selectedOptions[0]} />
                        );
                      }
                      return (
                        <Text as="span">
                          {selectedOptions.length} pipelines
                        </Text>
                      );
                    }}
                  />
                </div>
              </PermissionGuard>
            )}
            <PermissionGuard
              renderIfHasPermissions={[
                ScheduledEventsPermissions.GET_MY_DEAL_EVENTS,
                ScheduledEventsPermissions.GET_EVENTS,
              ]}
            >
              <PermissionGuard
                renderIfHasPermissions={[AccountsPermissions.GET_USERS]}
              >
                {accountTeams.length > 0 && (
                  <PermissionGuard
                    renderIfHasPermissions={[AccountsPermissions.GET_TEAMS]}
                  >
                    <div className="w-40">
                      <Combobox
                        multiple={true}
                        sizeVariant={SelectSizeVariant.SM}
                        placeholder="All teams"
                        value={selectedTeamIds}
                        onChange={handleTeamSelectionChange}
                        options={accountTeams.map((team) => ({
                          label: team.name,
                          value: team.id,
                        }))}
                        CustomSelected={({ selectedOptions }) => {
                          if (selectedOptions.length === 0) return null;
                          return (
                            <span>
                              {selectedOptions
                                .map((option) => option.label)
                                .join(', ')}
                            </span>
                          );
                        }}
                      />
                    </div>
                  </PermissionGuard>
                )}
                <AccountUserAvatarSelector
                  selectedUserIds={selectedPublicIds}
                  onChange={handleSelectionChange}
                  includeUnassigned
                  disabled={isLoadingAccountUsers}
                  filterUserPublicIds={teamMemberPublicIds}
                />
              </PermissionGuard>
            </PermissionGuard>
          </div>
          <PermissionGuard
            renderIfHasPermissions={[
              DispatchPermissions.PREVIEW_DISPATCH,
              DispatchPermissions.GET_EVENTS,
            ]}
          >
            <ButtonRadioGroup
              value={displayType}
              onChange={(displayType) => {
                onUpdate({ displayType, location: null });
              }}
              options={[
                { label: 'Map', value: 'map' },
                { label: 'Calendar', value: 'calendar' },
              ]}
              sizeVariant={'sm'}
            />
          </PermissionGuard>
          {displayType === 'calendar' && !hideCreateEventButton && (
            <>
              <PermissionGuard
                renderIfHasPermissions={[
                  ScheduledEventsPermissions.ADD_EVENT,
                  ScheduledEventsPermissions.ADD_MY_EVENT,
                ]}
              >
                <AnchorButton as={Link} to={`${url}/new`} size={'sm'}>
                  Create event
                </AnchorButton>
              </PermissionGuard>
            </>
          )}
          {displayType === 'map' && (
            <PermissionGuard
              renderIfHasPermissions={[
                ScheduledEventsPermissions.ADD_EVENT,
                ScheduledEventsPermissions.ADD_MY_EVENT,
              ]}
            >
              <AddressLookupControl
                onChange={(change) =>
                  onUpdate({
                    location: {
                      line1: change.line1,
                      line2: change.line2,
                      city: change.city,
                      administrativeArea: change.administrativeArea,
                      country: change.country,
                      postcode: change.postcode,
                    },
                  })
                }
                placeholder="Location search"
              />
            </PermissionGuard>
          )}
        </div>
      </PermissionGuard>
      <ErrorBoundary>
        <ScheduleFilters />
      </ErrorBoundary>
    </div>
  );
};
export default ScheduleDispatchFilters;
